/**
 * THE ONE PLACE A LISTING'S CITY AND STATE ARE JOINED FOR DISPLAY.
 *
 * Deliberate, identical twin of backend/src/common/utils/listing-place.util.ts.
 * Read that file's header for the full rationale; the short version:
 *
 * `city` is required on the normalized listing schema, so a posting that names
 * no city cannot leave the slot empty and ingestion fills it with SOMETHING --
 * the literal 'Unknown', or the state itself ("Michigan: ongoing full time CRNA
 * locum needs at 2 sites"). Neither is a city, and `${city}, ${state}` rendered
 * them as "Unknown, MI" and "MI, MI".
 *
 * Both columns independently mix USPS codes with full names, so the comparison
 * goes through a code lookup and collapses `MI`/`MI`, `Michigan`/`MI` and
 * `MI`/`Michigan` alike. A two-letter string that is some OTHER state's code is
 * left alone.
 *
 * NEW YORK IS EXEMPT. New York City is a real place and by far the largest
 * city-equals-state bucket in the data. It is the only such collision in the
 * US: every other city carrying a state's name sits in a DIFFERENT state
 * (Wyoming MI, Nevada MO, Delaware OH), which the code comparison already
 * leaves alone.
 *
 * Carries its own name->code map rather than reusing src/utils/usStates.js,
 * which has no Puerto Rico entry -- the same gap that makes the backend read
 * `stateNameToCode` from external-listings instead of `findState`.
 */

const STATE_CODES = {
  alabama: 'AL', alaska: 'AK', arizona: 'AZ', arkansas: 'AR',
  california: 'CA', colorado: 'CO', connecticut: 'CT', delaware: 'DE',
  'district of columbia': 'DC', florida: 'FL', georgia: 'GA', hawaii: 'HI',
  idaho: 'ID', illinois: 'IL', indiana: 'IN', iowa: 'IA',
  kansas: 'KS', kentucky: 'KY', louisiana: 'LA', maine: 'ME',
  maryland: 'MD', massachusetts: 'MA', michigan: 'MI', minnesota: 'MN',
  mississippi: 'MS', missouri: 'MO', montana: 'MT', nebraska: 'NE',
  nevada: 'NV', 'new hampshire': 'NH', 'new jersey': 'NJ', 'new mexico': 'NM',
  'new york': 'NY', 'north carolina': 'NC', 'north dakota': 'ND', ohio: 'OH',
  oklahoma: 'OK', oregon: 'OR', pennsylvania: 'PA', 'rhode island': 'RI',
  'south carolina': 'SC', 'south dakota': 'SD', tennessee: 'TN', texas: 'TX',
  utah: 'UT', vermont: 'VT', virginia: 'VA', washington: 'WA',
  'west virginia': 'WV', wisconsin: 'WI', wyoming: 'WY', 'puerto rico': 'PR',
};

/** Full US state name -> 2-letter code; passes a 2-letter code through; else null. */
const toStateCode = (value) => {
  if (typeof value !== 'string') return null;
  const s = value.trim();
  if (!s) return null;
  if (/^[A-Za-z]{2}$/.test(s)) return s.toUpperCase();
  return STATE_CODES[s.toLowerCase()] || null;
};

/** Values that occupy the slot without naming a place. */
const PLACEHOLDER_PLACE = /^(?:unknown|n\/?a|none|tbd|location tbd)$/i;

const clean = (value) =>
  typeof value === 'string' ? value.trim().replace(/\s+/g, ' ') : '';

/**
 * @returns {{city: string, state: string, location: string}}
 *   `location` is 'City, ST' | 'ST' | 'City' | '' -- never a bare separator,
 *   never a repeat. Callers supply their own copy for the empty case.
 */
export const listingPlace = (cityValue, stateValue) => {
  const rawState = clean(stateValue);
  const state = PLACEHOLDER_PLACE.test(rawState)
    ? ''
    : /^[a-z]{2}$/i.test(rawState)
      ? rawState.toUpperCase()
      : rawState;

  const rawCity = clean(cityValue);
  const cityCode = toStateCode(rawCity);
  const cityIsItsOwnState =
    cityCode !== null &&
    cityCode === toStateCode(state) &&
    rawCity.toLowerCase() !== 'new york';

  const city =
    !rawCity || PLACEHOLDER_PLACE.test(rawCity) || cityIsItsOwnState
      ? ''
      : rawCity;

  return { city, state, location: [city, state].filter(Boolean).join(', ') };
};

export default listingPlace;
